import React from "react";
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FiCheckCircle, FiHome, FiUploadCloud } from "react-icons/fi";
import { IconToggleCard } from "./IconToggleCard";
import { files, roles } from "../../services/api";
import { getMessageImage } from "../../utils/messages";

interface MessageFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

const buildInitialData = (initialData?: any) => {
  const data = initialData || {};
  const imageId =
    typeof data.image === "object" && data.image !== null
      ? data.image._id || ""
      : data.image || "";
  const result = {
    title: "",
    body: "",
    cta: {
      label: "",
      url: "",
      type: "internal",
      openInNewTab: false,
    },
    showInHome: false,
    audienceType: "all",
    isActive: true,
    ...data,
    image: imageId,
  };

  result.targetRoles = Array.isArray(data.targetRoles) ? data.targetRoles : [];
  result.cta = {
    label: data.cta?.label || "",
    url: data.cta?.url || "",
    type: data.cta?.type === "external" ? "external" : "internal",
    openInNewTab: Boolean(data.cta?.openInNewTab),
  };

  return result;
};

export const MessageForm: React.FC<MessageFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
}) => {
  const toast = useToast();
  const [formData, setFormData] = React.useState<any>(() =>
    buildInitialData(initialData),
  );
  const [selectedImageFile, setSelectedImageFile] =
    React.useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState("");
  const [isUploadingImage, setIsUploadingImage] = React.useState(false);

  React.useEffect(() => {
    setFormData(buildInitialData(initialData));
    setSelectedImageFile(null);
    setImagePreviewUrl(initialData?.image ? getMessageImage(initialData) : "");
  }, [initialData]);

  React.useEffect(() => {
    return () => {
      if (imagePreviewUrl.startsWith("blob:")) {
        window.URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const { data: rolesResponse } = useQuery({
    queryKey: ["roles", "all-for-messages"],
    queryFn: () => roles.getAll(1, 100) as Promise<any>,
  });

  const roleItems = rolesResponse?.roles || [];

  const inputBg = useColorModeValue("white", "#23272F");
  const inputBorder = useColorModeValue("#e5e7eb", "#444");
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const sectionBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const textColor = useColorModeValue("gray.800", "white");

  const fieldProps = {
    borderRadius: "lg",
    bg: inputBg,
    borderColor: inputBorder,
    boxShadow: "sm",
    fontSize: "md",
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;

    setFormData((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "audienceType" && value === "all" ? { targetRoles: [] } : {}),
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = e.target.files?.[0] || null;

    if (!nextFile) {
      return;
    }

    if (imagePreviewUrl.startsWith("blob:")) {
      window.URL.revokeObjectURL(imagePreviewUrl);
    }

    const nextPreviewUrl = window.URL.createObjectURL(nextFile);
    setSelectedImageFile(nextFile);
    setImagePreviewUrl(nextPreviewUrl);
  };

  const handleRemoveImage = () => {
    if (imagePreviewUrl.startsWith("blob:")) {
      window.URL.revokeObjectURL(imagePreviewUrl);
    }

    setSelectedImageFile(null);
    setImagePreviewUrl("");
    setFormData((prev: any) => ({ ...prev, image: null }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      ...formData,
      cta: {
        label: formData.cta?.label || "",
        url: formData.cta?.url || "",
        type: formData.cta?.type === "external" ? "external" : "internal",
        openInNewTab: Boolean(formData.cta?.openInNewTab),
      },
      targetRoles:
        formData.audienceType === "roles" ? formData.targetRoles || [] : [],
    };

    try {
      if (selectedImageFile) {
        setIsUploadingImage(true);
        const uploaded = await files.upload(selectedImageFile);
        payload.image = uploaded.file._id;
      }
    } catch (error: any) {
      toast({
        title: "خطا در آپلود تصویر پیام",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    } finally {
      setIsUploadingImage(false);
    }

    await Promise.resolve(onSubmit(payload));
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={5} align="stretch" w="100%" py="10px">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <IconToggleCard
            name="isActive"
            label="فعال"
            icon={FiCheckCircle}
            isChecked={Boolean(formData.isActive)}
            onChange={handleChange}
          />
          <IconToggleCard
            name="showInHome"
            label="نمایش در صفحه اصلی"
            icon={FiHome}
            isChecked={Boolean(formData.showInHome)}
            onChange={handleChange}
          />
        </SimpleGrid>

        <FormControl>
          <FormLabel fontWeight="bold" color={labelColor}>
            تصویر پیام
          </FormLabel>
          <Box
            border="1px dashed"
            borderColor={
              selectedImageFile || formData.image ? "green.300" : inputBorder
            }
            borderRadius="xl"
            p={4}
            bg={sectionBg}
          >
            <VStack spacing={3}>
              {imagePreviewUrl ? (
                <Box
                  overflow="hidden"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={inputBorder}
                  w="100%"
                >
                  <Image
                    src={imagePreviewUrl}
                    alt="message preview"
                    w="100%"
                    maxH="240px"
                    objectFit="cover"
                  />
                </Box>
              ) : null}
              <Icon
                verticalAlign="middle"
                as={FiUploadCloud as any}
                boxSize={6}
                color={
                  selectedImageFile || formData.image ? "green.500" : "brand.500"
                }
              />
              <Text fontSize="sm" fontWeight="bold" color={textColor}>
                {selectedImageFile?.name ||
                  (formData.image
                    ? "تصویر فعلی پیام انتخاب شده است."
                    : "تصویر پیام را بارگذاری کنید")}
              </Text>
              <Button
                as="label"
                htmlFor="message-image"
                size="sm"
                colorScheme="brand"
                variant={selectedImageFile || formData.image ? "outline" : "solid"}
                cursor="pointer"
                isDisabled={isUploadingImage}
              >
                انتخاب تصویر
              </Button>
              <Input
                id="message-image"
                type="file"
                accept="image/*"
                display="none"
                onChange={handleImageChange}
              />
              <FormHelperText m={0}>
                این تصویر در مودال صفحه اصلی و جزئیات پیام نمایش داده می‌شود.
              </FormHelperText>
              {imagePreviewUrl || formData.image ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={handleRemoveImage}
                  isDisabled={isUploadingImage}
                >
                  حذف تصویر
                </Button>
              ) : null}
            </VStack>
          </Box>
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontWeight="bold" color={labelColor}>
            عنوان
          </FormLabel>
          <Input
            name="title"
            value={formData.title || ""}
            onChange={handleChange}
            {...fieldProps}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontWeight="bold" color={labelColor}>
            متن پیام
          </FormLabel>
          <Textarea
            name="body"
            value={formData.body || ""}
            onChange={handleChange}
            rows={6}
            {...fieldProps}
          />
        </FormControl>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl>
            <FormLabel fontWeight="bold" color={labelColor}>
              متن دکمه
            </FormLabel>
            <Input
              value={formData.cta?.label || ""}
              onChange={(e) =>
                setFormData((prev: any) => ({
                  ...prev,
                  cta: { ...prev.cta, label: e.target.value },
                }))
              }
              placeholder="مثلا مشاهده جزئیات"
              {...fieldProps}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontWeight="bold" color={labelColor}>
              نوع لینک
            </FormLabel>
            <Select
              value={formData.cta?.type || "internal"}
              onChange={(e) =>
                setFormData((prev: any) => ({
                  ...prev,
                  cta: { ...prev.cta, type: e.target.value },
                }))
              }
              {...fieldProps}
            >
              <option value="internal">مسیر داخلی</option>
              <option value="external">لینک خارجی</option>
            </Select>
          </FormControl>
        </SimpleGrid>

        <FormControl>
          <FormLabel fontWeight="bold" color={labelColor}>
            لینک دکمه
          </FormLabel>
          <Input
            value={formData.cta?.url || ""}
            onChange={(e) =>
              setFormData((prev: any) => ({
                ...prev,
                cta: { ...prev.cta, url: e.target.value },
              }))
            }
            placeholder={
              formData.cta?.type === "external"
                ? "https://example.com"
                : "/funds"
            }
            {...fieldProps}
          />
          <FormHelperText>
            برای لینک داخلی مسیرهایی مثل <code>/funds</code> و برای لینک خارجی
            آدرس کامل وارد کنید.
          </FormHelperText>
        </FormControl>

        <Checkbox
          isChecked={Boolean(formData.cta?.openInNewTab)}
          onChange={(e) =>
            setFormData((prev: any) => ({
              ...prev,
              cta: { ...prev.cta, openInNewTab: e.target.checked },
            }))
          }
        >
          باز شدن در تب جدید
        </Checkbox>

        <FormControl isRequired>
          <FormLabel fontWeight="bold" color={labelColor}>
            مخاطب
          </FormLabel>
          <Select
            name="audienceType"
            value={formData.audienceType || "all"}
            onChange={handleChange}
            {...fieldProps}
          >
            <option value="all">همه کاربران مجاز</option>
            <option value="roles">نقش‌های مشخص</option>
          </Select>
        </FormControl>

        {formData.audienceType === "roles" ? (
          <FormControl isRequired>
            <FormLabel fontWeight="bold" color={labelColor}>
              نقش‌های هدف
            </FormLabel>
            <CheckboxGroup
              value={formData.targetRoles || []}
              onChange={(values) =>
                setFormData((prev: any) => ({ ...prev, targetRoles: values }))
              }
            >
              <Stack spacing={3}>
                {roleItems.map((role: any) => (
                  <Checkbox key={role._id} value={role.value}>
                    {role.label}
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
            <FormHelperText>
              فقط کاربران دارای این نقش‌ها پیام را در داشبورد خواهند دید.
            </FormHelperText>
          </FormControl>
        ) : null}

        <HStack justify="end" pt={2}>
          <Button type="button" variant="ghost" onClick={onCancel}>
            لغو
          </Button>
          <Button type="submit" colorScheme="brand" isLoading={isUploadingImage}>
            ارسال
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};
